import * as React from 'react';
import './StatTile.css';

interface StatTileProps {
    label: string;
    /** A domain hue, e.g. 'var(--domain-crashes)'. Colours the chip and sparkline. */
    accent?: string;
    icon?: React.ReactNode;
    value: string;
    /** Rendered smaller beside the value, e.g. "min". */
    unit?: string;
    meta?: string;
    /** Optional sparkline series, oldest first. */
    trend?: number[];
    onInfo?: () => void;
}

const SPARK_WIDTH = 132;
const SPARK_HEIGHT = 28;

/** Inline sparkline: 2px line, one end marker. No chart library needed. */
function Sparkline({series}: {series: number[]}) {
    if (series.length < 2) {
        return null;
    }
    const min = Math.min(...series);
    const max = Math.max(...series);
    const span = max - min || 1;
    // Inset by the marker radius so the end dot is never clipped.
    const pad = 4;
    const points = series.map((value, i) => {
        const x = pad + (i / (series.length - 1)) * (SPARK_WIDTH - pad * 2);
        const y = pad + (1 - (value - min) / span) * (SPARK_HEIGHT - pad * 2);
        return [x, y] as const;
    });
    const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const [lastX, lastY] = points[points.length - 1];

    return (
        <svg
            className="stat-tile__spark"
            width={SPARK_WIDTH}
            height={SPARK_HEIGHT}
            viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
            aria-hidden="true"
            focusable="false"
        >
            <path
                d={path}
                fill="none"
                stroke="var(--accent-track)"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            {/* Surface ring keeps the marker legible where it crosses the line. */}
            <circle cx={lastX} cy={lastY} r={4} fill="var(--accent)" stroke="var(--surface-1)" strokeWidth={2} />
        </svg>
    );
}

/**
 * A headline number. The number is the chart -- this replaces the gauge
 * doughnuts, which drew a two-slice ring around a single value.
 */
const StatTile: React.FC<StatTileProps> = (props: StatTileProps) => {
    const content = (
        <>
            <span className="stat-tile__head">
                {props.icon && <span className="stat-tile__chip" aria-hidden="true">{props.icon}</span>}
                {/* The value stays in ink: the chip beside it carries the hue,
                    so colour is never the only thing naming the measure. */}
                <span className="stat-tile__label">{props.label}</span>
            </span>
            <span className="stat-tile__value-row">
                <span className="stat-tile__value">{props.value}</span>
                {props.unit && <span className="stat-tile__unit">{props.unit}</span>}
            </span>
            {props.trend && <Sparkline series={props.trend} />}
            {props.meta && <span className="stat-tile__meta">{props.meta}</span>}
        </>
    );

    const style = props.accent ? ({'--accent': props.accent} as React.CSSProperties) : undefined;

    if (!props.onInfo) {
        return <div className="stat-tile has-accent" style={style}>{content}</div>;
    }
    return (
        <button type="button" className="stat-tile has-accent" style={style} onClick={props.onInfo}>
            {content}
        </button>
    );
};

export default StatTile;
